import React, { useState, useEffect } from 'react';
import { Modal, Button, Progress, message } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import '../assets/styles/SkillQuiz.css';

const SkillQuiz = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [loginPromptVisible, setLoginPromptVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const navigate = useNavigate();

  const quizQuestions = [
    {
      question: "What does AI stand for?",
      options: ["Artificial Intelligence", "Automated Integration", "Advanced Informatics", "Algorithmic Insight"],
      correctAnswer: "Artificial Intelligence",
      explanation: "AI stands for Artificial Intelligence, which refers to the simulation of human intelligence in machines."
    },
    {
      question: "Which of these is a common cybersecurity threat?",
      options: ["Phishing", "Cloud Computing", "Data Mining", "API Integration"],
      correctAnswer: "Phishing",
      explanation: "Phishing is a cybersecurity threat where attackers trick users into providing sensitive information, often via fake emails or websites."
    },
    {
      question: "What is a key benefit of Data Science?",
      options: ["Faster Hardware", "Predictive Insights", "Improved Graphics", "Lower Costs"],
      correctAnswer: "Predictive Insights",
      explanation: "Data Science enables predictive insights by analyzing data to forecast trends and outcomes."
    },
    {
      question: "Which programming language is commonly used in AI development?",
      options: ["Python", "HTML", "CSS", "SQL"],
      correctAnswer: "Python",
      explanation: "Python is widely used in AI due to its simplicity and extensive libraries like TensorFlow and PyTorch."
    },
    {
      question: "What does SQL stand for?",
      options: ["Structured Query Language", "Simple Query Logic", "System Query Layer", "Sequential Query Language"],
      correctAnswer: "Structured Query Language",
      explanation: "SQL (Structured Query Language) is used to manage and manipulate relational databases."
    },
    {
      question: "Which of these is a version control system?",
      options: ["Git", "Docker", "Kubernetes", "Jenkins"],
      correctAnswer: "Git",
      explanation: "Git is a distributed version control system for tracking changes in source code."
    },
    {
      question: "What is the primary purpose of a firewall?",
      options: ["Monitor Network Traffic", "Increase Internet Speed", "Store Data", "Run Applications"],
      correctAnswer: "Monitor Network Traffic",
      explanation: "A firewall monitors and controls network traffic to protect systems from unauthorized access."
    },
    {
      question: "Which algorithm is commonly used for supervised learning?",
      options: ["K-Means", "Linear Regression", "Principal Component Analysis", "Apriori"],
      correctAnswer: "Linear Regression",
      explanation: "Linear Regression is a supervised learning algorithm used to predict continuous outcomes."
    },
    {
      question: "What does HTTP stand for in web development?",
      options: ["HyperText Transfer Protocol", "High Traffic Transport Protocol", "Hyperlink Text Tool Protocol", "Host Transfer Text Process"],
      correctAnswer: "HyperText Transfer Protocol",
      explanation: "HTTP (HyperText Transfer Protocol) is the foundation of data communication on the web."
    },
    {
      question: "Which of these is a NoSQL database?",
      options: ["MySQL", "PostgreSQL", "MongoDB", "SQLite"],
      correctAnswer: "MongoDB",
      explanation: "MongoDB is a NoSQL database that stores data in a flexible, document-oriented format."
    },
    {
      question: "What is the purpose of a 'for' loop in programming?",
      options: ["To repeat a block of code", "To define a function", "To store data", "To connect to a database"],
      correctAnswer: "To repeat a block of code",
      explanation: "A 'for' loop repeats a block of code a specified number of times or over a range."
    },
    {
      question: "Which of these is a cloud computing service?",
      options: ["AWS", "Photoshop", "Excel", "Notepad"],
      correctAnswer: "AWS",
      explanation: "AWS (Amazon Web Services) provides on-demand cloud computing services."
    },
    {
      question: "What does OOP stand for in programming?",
      options: ["Object-Oriented Programming", "Operational Output Processing", "Optimized Order Protocol", "Open Office Platform"],
      correctAnswer: "Object-Oriented Programming",
      explanation: "OOP (Object-Oriented Programming) is a paradigm based on objects and classes."
    },
    {
      question: "Which tool is used for containerization?",
      options: ["Docker", "GitHub", "JIRA", "VS Code"],
      correctAnswer: "Docker",
      explanation: "Docker is a tool for creating and managing containers to run applications."
    },
    {
      question: "What is the main goal of machine learning?",
      options: ["To enable computers to learn from data", "To increase internet speed", "To design user interfaces", "To manage databases"],
      correctAnswer: "To enable computers to learn from data",
      explanation: "Machine learning aims to train computers to learn patterns from data without explicit programming."
    },
    {
      question: "Which of these is a supervised learning technique?",
      options: ["Decision Trees", "K-Means Clustering", "DBSCAN", "t-SNE"],
      correctAnswer: "Decision Trees",
      explanation: "Decision Trees are a supervised learning method used for classification and regression tasks."
    },
    {
      question: "What is the purpose of normalization in data preprocessing?",
      options: ["To scale data to a standard range", "To remove duplicates", "To encrypt data", "To increase storage"],
      correctAnswer: "To scale data to a standard range",
      explanation: "Normalization scales data (e.g., to 0-1) to improve model performance."
    },
    {
      question: "Which protocol is used for secure web communication?",
      options: ["HTTP", "FTP", "HTTPS", "SMTP"],
      correctAnswer: "HTTPS",
      explanation: "HTTPS (HyperText Transfer Protocol Secure) encrypts data for secure web communication."
    },
    {
      question: "What is overfitting in machine learning?",
      options: ["Model learns noise instead of patterns", "Model is too simple", "Model runs too fast", "Model lacks data"],
      correctAnswer: "Model learns noise instead of patterns",
      explanation: "Overfitting occurs when a model learns noise or specific details from training data, reducing generalization."
    },
    {
      question: "Which of these is a benefit of using APIs?",
      options: ["Improved modularity", "Faster hardware", "Better graphics", "Reduced memory usage"],
      correctAnswer: "Improved modularity",
      explanation: "APIs (Application Programming Interfaces) allow modular integration of services and functionalities."
    },
    {
      question: "Which of these is an unsupervised learning algorithm?",
      options: ["Support Vector Machines", "K-Means Clustering", "Random Forest", "Gradient Boosting"],
      correctAnswer: "K-Means Clustering",
      explanation: "K-Means Clustering is an unsupervised learning algorithm used to group data into clusters without labeled outputs."
    },
    {
      question: "What does 'GPU' stand for in computing?",
      options: ["General Processing Unit", "Graphics Processing Unit", "Global Power Unit", "Grid Programming Utility"],
      correctAnswer: "Graphics Processing Unit",
      explanation: "GPU (Graphics Processing Unit) is specialized hardware for parallel processing, often used in AI and gaming."
    },
    {
      question: "Which of these is a common data visualization tool?",
      options: ["Tableau", "Nginx", "Redis", "Apache"],
      correctAnswer: "Tableau",
      explanation: "Tableau is a popular tool for creating interactive data visualizations and dashboards."
    },
    {
      question: "What is the purpose of a 'try-catch' block in programming?",
      options: ["To handle exceptions", "To loop through data", "To define variables", "To optimize code"],
      correctAnswer: "To handle exceptions",
      explanation: "A 'try-catch' block is used to handle errors (exceptions) and prevent program crashes."
    },
    {
      question: "Which of these is a type of neural network?",
      options: ["Convolutional Neural Network", "Binary Search Tree", "Hash Table", "Linked List"],
      correctAnswer: "Convolutional Neural Network",
      explanation: "Convolutional Neural Networks (CNNs) are used in deep learning, especially for image processing."
    },
    {
      question: "What is a 'hash function' used for?",
      options: ["To map data to a fixed-size value", "To sort data", "To encrypt files", "To compress images"],
      correctAnswer: "To map data to a fixed-size value",
      explanation: "A hash function maps data to a fixed-size value (hash), often used in data structures like hash tables."
    },
    {
      question: "Which of these is a principle of Agile development?",
      options: ["Iterative Development", "Waterfall Phases", "Fixed Requirements", "Single Deployment"],
      correctAnswer: "Iterative Development",
      explanation: "Agile emphasizes iterative development, delivering small, incremental improvements frequently."
    },
    {
      question: "What does 'IoT' stand for?",
      options: ["Internet of Things", "Input Output Terminal", "Integrated Operations Tool", "Internet Operations Team"],
      correctAnswer: "Internet of Things",
      explanation: "IoT (Internet of Things) refers to connected devices communicating over the internet."
    },
    {
      question: "Which of these is a type of encryption?",
      options: ["AES", "JSON", "XML", "CSV"],
      correctAnswer: "AES",
      explanation: "AES (Advanced Encryption Standard) is a widely used symmetric encryption algorithm."
    },
    {
      question: "What is the purpose of 'cross-validation' in machine learning?",
      options: ["To evaluate model performance", "To clean data", "To increase speed", "To reduce features"],
      correctAnswer: "To evaluate model performance",
      explanation: "Cross-validation splits data into subsets to assess how well a model generalizes to unseen data."
    },
    // Additional questions
    {
      question: "Which of the following is NOT a Python feature?",
      options: ["Interpreted", "Case-sensitive", "Object-oriented", "Compiled"],
      correctAnswer: "Compiled",
      explanation: "Python is an interpreted language, not compiled."
    },
    {
      question: "What does DRY stand for in programming?",
      options: ["Don't Repeat Yourself", "Data Retrieval Year", "Digital Rights Yield", "Dynamic Resource Yield"],
      correctAnswer: "Don't Repeat Yourself",
      explanation: "DRY is a principle aimed at reducing repetition of code."
    },
    {
      question: "Which of these is a front-end framework?",
      options: ["React", "Django", "Flask", "Express"],
      correctAnswer: "React",
      explanation: "React is a JavaScript library for building user interfaces."
    },
    {
      question: "What is the purpose of a 'foreign key' in a database?",
      options: ["To establish relationships between tables", "To store encrypted data", "To optimize query performance", "To validate user input"],
      correctAnswer: "To establish relationships between tables",
      explanation: "Foreign keys enforce relationships between tables in a relational database."
    },
    {
      question: "Which of these is a Big O notation for constant time complexity?",
      options: ["O(1)", "O(n)", "O(log n)", "O(n²)"],
      correctAnswer: "O(1)",
      explanation: "O(1) represents constant time complexity, meaning the operation takes the same time regardless of input size."
    },
    {
      question: "What does 'REST' stand for in web services?",
      options: ["Representational State Transfer", "Relational Entity Service Technology", "Remote Execution and Storage Transfer", "Real-time Event Streaming Technology"],
      correctAnswer: "Representational State Transfer",
      explanation: "REST is an architectural style for designing networked applications."
    },
    {
      question: "Which data structure uses a LIFO principle?",
      options: ["Stack", "Queue", "Linked List", "Tree"],
      correctAnswer: "Stack",
      explanation: "Stack follows Last-In-First-Out (LIFO) principle where the last element added is the first to be removed."
    },
    {
      question: "What is the main purpose of a 'unit test'?",
      options: ["To test the entire application", "To test individual components", "To test user interfaces", "To test database connections"],
      correctAnswer: "To test individual components",
      explanation: "Unit tests verify the correctness of individual software components."
    },
    {
      question: "Which of these is a NoSQL database?",
      options: ["Cassandra", "Oracle", "SQL Server", "PostgreSQL"],
      correctAnswer: "Cassandra",
      explanation: "Cassandra is a distributed NoSQL database management system."
    },
    {
      question: "What does 'CI/CD' stand for?",
      options: ["Continuous Integration/Continuous Deployment", "Cloud Infrastructure/Cloud Deployment", "Code Integration/Code Delivery", "Centralized Integration/Continuous Development"],
      correctAnswer: "Continuous Integration/Continuous Deployment",
      explanation: "CI/CD is a set of practices for automating software delivery processes."
    }
  ];

  // Fisher-Yates shuffle algorithm
  const shuffleQuestions = (questions) => {
    const shuffled = [...questions];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    // Shuffle questions on component mount
    setShuffledQuestions(shuffleQuestions(quizQuestions));
  }, []);

  useEffect(() => {
    if (isVisible && !quizCompleted && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleAnswer(null);
            return 30;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isVisible, quizCompleted, timeLeft, currentQuestion]);

  const handleAnswer = (answer) => {
    const currentQ = shuffledQuestions[currentQuestion];
    const isCorrect = answer === currentQ.correctAnswer;
    
    if (isCorrect) {
      setScore(score + 1);
      message.success("Correct! " + currentQ.explanation, 3);
    } else {
      message.error("Wrong! " + currentQ.explanation, 3);
    }

    if (currentQuestion < shuffledQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setTimeLeft(30);
    } else {
      setQuizCompleted(true);
      setLoginPromptVisible(true);
      onComplete(score + (isCorrect ? 1 : 0), shuffledQuestions.length);
    }
  };

  const handleRetry = () => {
    setCurrentQuestion(0);
    setScore(0);
    setQuizCompleted(false);
    setTimeLeft(30);
    setShuffledQuestions(shuffleQuestions(quizQuestions));
  };

  const handleLoginPrompt = () => {
    setLoginPromptVisible(false);
    navigate('/signin');
  };

  const percentage = Math.round((score / shuffledQuestions.length) * 100);

  return (
    <>
      <Button type="primary" onClick={() => setIsVisible(true)} style={{ margin: '20px 0' }}>
        Test Your Skills Now!
      </Button>
      <Modal
        title="JectHub Skill Assessment Quiz"
        open={isVisible}
        onCancel={() => setIsVisible(false)}
        footer={null}
        width={700}
        className="skill-quiz-modal"
      >
        {!quizCompleted ? (
          <div className="quiz-content">
            <Progress percent={((currentQuestion + 1) / shuffledQuestions.length) * 100} showInfo={false} />
            <h3>{shuffledQuestions[currentQuestion]?.question}</h3>
            <div className="options-container">
              {shuffledQuestions[currentQuestion]?.options.map((option, index) => (
                <Button
                  key={index}
                  onClick={() => handleAnswer(option)}
                  className="option-button"
                >
                  {option}
                </Button>
              ))}
            </div>
            <div className="quiz-info">
              <p>Question {currentQuestion + 1} of {shuffledQuestions.length}</p>
              <p>Time Left: {timeLeft}s</p>
            </div>
          </div>
        ) : (
          <div className="quiz-result">
            <h3>Quiz Complete!</h3>
            <Progress type="circle" percent={percentage} />
            <p>Your Score: {score} out of {shuffledQuestions.length}</p>
            <p className="feedback-text">
              {percentage >= 80
                ? "Excellent work! You're ready for our advanced courses."
                : percentage >= 60
                ? "Good job! Consider our intermediate courses to level up."
                : percentage >= 40
                ? "Nice effort! Our beginner courses can help you improve."
                : "No worries! Start with our free introductory lessons."}
            </p>
            <div className="result-buttons">
              <Link to="/courses">
                <Button type="primary">Explore Courses</Button>
              </Link>
              <Button onClick={handleRetry} style={{ marginLeft: '10px' }}>
                Retry Quiz
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Save Your Progress!"
        open={loginPromptVisible}
        onCancel={() => setLoginPromptVisible(false)}
        footer={[
          <Button key="later" onClick={() => setLoginPromptVisible(false)}>
            Maybe Later
          </Button>,
          <Button key="login" type="primary" onClick={handleLoginPrompt}>
            Log In Now
          </Button>,
        ]}
      >
        <p>
          Log in to save your quiz results and unlock personalized course recommendations!
        </p>
      </Modal>
    </>
  );
};

export default SkillQuiz;